import type { Fund } from "@/data/funds";

export type LeveragedSubcategory = "single-stock" | "thematic" | "core-equity";

/**
 * Returns true if the leveraged fund tracks a single common stock.
 * Rule: underlyingTicker is a non-empty real ticker (not "" and not "[ ]")
 * AND underlyingIssuer is non-null.
 */
export function isSingleStockLeveraged(
  fund: Pick<Fund, "fundType" | "underlyingTicker" | "underlyingIssuer">,
): boolean {
  if (fund.fundType !== "leveraged") return false;
  const ut = fund.underlyingTicker?.trim();
  if (!ut || ut === "[ ]") return false;
  if (fund.underlyingIssuer == null) return false;
  return true;
}

/**
 * Classify a leveraged fund into its reference sub-category.
 * Returns null for non-leveraged funds.
 *
 * `thematicTickers` = set of OUR thematic funds' tickers (UPPERCASE),
 * built from the full live fund list via `thematicTickerSet`.
 */
export function leveragedSubcategory(
  fund: Fund,
  thematicTickers: ReadonlySet<string>,
): LeveragedSubcategory | null {
  if (fund.fundType !== "leveraged") return null;

  if (isSingleStockLeveraged(fund)) return "single-stock";

  const ut = fund.underlyingTicker?.trim().toUpperCase();
  if (ut && thematicTickers.has(ut)) return "thematic";

  return "core-equity";
}

/** Build the thematic-ticker set from the full fund list (fundType==="thematic"). */
export function thematicTickerSet(funds: readonly Fund[]): Set<string> {
  const result = new Set<string>();
  for (const fund of funds) {
    if (fund.fundType === "thematic") {
      result.add(fund.ticker.toUpperCase());
    }
  }
  return result;
}
